import type { CountryFromApi } from "./countryFlag";

export type SignupGradeSource = {
  data?: Array<{ gradeId: string; gradeName: number | string }>;
};

export const toOrdinal = (num: number) => {
  const lastTwo = num % 100;
  if (lastTwo >= 11 && lastTwo <= 13) return `${num}th`;
  switch (num % 10) {
    case 1:
      return `${num}st`;
    case 2:
      return `${num}nd`;
    case 3:
      return `${num}rd`;
    default:
      return `${num}th`;
  }
};

export const formatSignupGradeDisplay = (
  grade: number | string | undefined,
  gradeLabel = "Grade",
): string => {
  if (grade == null || grade === "") return gradeLabel;
  const parsed =
    typeof grade === "number" ? grade : parseInt(String(grade), 10);
  if (Number.isNaN(parsed)) return String(grade);
  return `${gradeLabel} ${toOrdinal(parsed)}`;
};

export const getSignupGradeOptions = (data?: SignupGradeSource) => {
  const grades = data?.data ?? [];
  const seenValues = new Set<string | number>();
  return grades
    .map((grade) => {
      const label: string = grade.gradeId ?? "";
      const value: string = toOrdinal(Number(grade.gradeName)) ?? "";
      return { label, value };
    })
    .filter((option: { value: string }) => {
      if (!option.value) return false;
      if (seenValues.has(option.value)) return false;
      seenValues.add(option.value);
      return true;
    });
};

export type SignupExamOption = { id: number; displayName: string };

export const getSignupLearningGoalOptions = (
  response:
    | {
        data?: Array<{
          gradeName: number | string;
          exams?: SignupExamOption[];
        }>;
      }
    | undefined,
  selectedGrade: number | undefined,
): SignupExamOption[] => {
  if (selectedGrade == null) return [];
  const options = new Map<number, SignupExamOption>();
  for (const grade of response?.data ?? []) {
    // The signup grade selector stores the numeric gradeName (e.g. 10).
    if (Number(grade.gradeName) !== selectedGrade) continue;
    for (const exam of grade.exams ?? []) {
      if (Number.isFinite(exam.id) && exam.id > 0 && exam.displayName) {
        options.set(exam.id, { id: exam.id, displayName: exam.displayName });
      }
    }
  }
  return [...options.values()];
};

export const INDIA_COUNTRY_CODE = "91";
export const DEFAULT_PARENT_PHONE_LENGTH = 10;

export const getSignupParentPhoneMaxLength = (country: CountryFromApi) => {
  const lengths = country.phoneLength?.filter(
    (length) => Number.isFinite(length) && length > 0,
  );

  return lengths?.length ? Math.max(...lengths) : DEFAULT_PARENT_PHONE_LENGTH;
};

export const getSignupFallbackParentCountry = (
  isdCode: string,
): CountryFromApi => {
  const phone = isdCode.replace(/\D/g, "") || INDIA_COUNTRY_CODE;
  return {
    code: "IN",
    phone,
    phoneLength: [DEFAULT_PARENT_PHONE_LENGTH],
  };
};

export const isIndianIsdCode = (isdCode: string) =>
  isdCode.replace(/\D/g, "") === INDIA_COUNTRY_CODE;

export type SignupTimezoneOption = {
  id: number;
  label: string;
  title?: string;
  subtitle?: string;
  utcOffset?: string;
  abbreviation?: string;
};

export type CountryTimezoneMapping = {
  id: number;
  name?: string;
  display_name?: string;
  tz_identifier?: string;
  [key: string]: unknown;
};

export type CountryMapping = {
  id: number;
  details: {
    name?: string;
    display_name?: string;
    code?: string;
    currencies?: unknown[];
    timezones?: CountryTimezoneMapping[];
    isd_codes?: Array<{
      id?: number;
      name?: string;
      display_name?: string;
      code?: string;
    }>;
    [key: string]: unknown;
  };
};

type TimezoneMappingDetails = {
  name?: unknown;
  display_name?: unknown;
  tz_identifier?: unknown;
};

type TimezoneMappingItem = {
  id?: unknown;
  details?: TimezoneMappingDetails | null;
};

type CountryMappingDetails = {
  timezones?: unknown;
  isd_codes?: unknown;
};

type CountryMappingItem = {
  details?: CountryMappingDetails | null;
};

type TimezoneMappingsResponse = {
  items?: Array<TimezoneMappingItem | CountryMappingItem>;
};

const getStringValue = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value.trim() : undefined;

const TIMEZONE_IDENTIFIER_ALIASES: Record<string, string> = {
  "America/Honolulu": "Pacific/Honolulu",
};

const normalizeTimezoneIdentifier = (timeZone: string): string =>
  TIMEZONE_IDENTIFIER_ALIASES[timeZone] ?? timeZone;

const getTimezoneOffsetLabel = (timeZone: string): string | undefined => {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: normalizeTimezoneIdentifier(timeZone),
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    const parts = formatter
      .formatToParts(now)
      .reduce<Record<string, string>>((acc, part) => {
        if (part.type !== "literal") acc[part.type] = part.value;
        return acc;
      }, {});
    const hour = Number(parts.hour === "24" ? "0" : parts.hour);
    const zonedTimestamp = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      hour,
      Number(parts.minute),
      Number(parts.second),
    );
    const offsetMinutes = Math.round(
      (zonedTimestamp - (now.getTime() - now.getMilliseconds())) / 60000,
    );
    const sign = offsetMinutes >= 0 ? "+" : "-";
    const absoluteMinutes = Math.abs(offsetMinutes);
    const hours = String(Math.floor(absoluteMinutes / 60)).padStart(2, "0");
    const minutes = String(absoluteMinutes % 60).padStart(2, "0");
    return `UTC${sign}${hours}:${minutes}`;
  } catch {
    return undefined;
  }
};

const hasMatchingIsdCode = (
  details: CountryMappingDetails,
  isdCode: string,
) => {
  const targetDigits = isdCode.replace(/\D/g, "");
  const isdCodes = Array.isArray(details.isd_codes) ? details.isd_codes : [];
  return isdCodes.some((item) => {
    if (!item || typeof item !== "object") return false;
    const candidate = item as {
      name?: unknown;
      display_name?: unknown;
      code?: unknown;
    };
    return [candidate.name, candidate.display_name, candidate.code]
      .map((value) => getStringValue(value)?.replace(/\D/g, ""))
      .some((digits) => digits === targetDigits);
  });
};

const getTimezoneItems = (
  value: unknown,
  isdCode: string,
): TimezoneMappingItem[] => {
  if (!value || typeof value !== "object") return [];
  const items = (value as TimezoneMappingsResponse).items;
  if (!Array.isArray(items)) return [];

  const hasCountryShape = items.some((item) => {
    const details = item.details as CountryMappingDetails | null | undefined;
    return (
      details != null &&
      (Array.isArray(details.timezones) || Array.isArray(details.isd_codes))
    );
  });
  const countryItem = items.find((item) => {
    const details = item.details as CountryMappingDetails | null | undefined;
    return details != null && hasMatchingIsdCode(details, isdCode);
  });
  const countryTimezones = (
    countryItem?.details as CountryMappingDetails | null | undefined
  )?.timezones;
  if (Array.isArray(countryTimezones)) {
    return countryTimezones as TimezoneMappingItem[];
  }

  return hasCountryShape ? [] : (items as TimezoneMappingItem[]);
};

const getPrimaryTimezoneAbbreviation = (
  abbreviation?: string,
): string | undefined => abbreviation?.split("/")[0]?.trim() || undefined;

const getTimezoneCityLabel = (identifier?: string): string | undefined => {
  const city = identifier?.split("/").pop()?.replace(/_/g, " ").trim();
  return city === "Honolulu" ? "Hawaii" : city || undefined;
};

const getFriendlyTimezoneName = (
  displayName?: string,
  abbreviation?: string,
  identifier?: string,
): string | undefined => {
  const primaryAbbreviation = getPrimaryTimezoneAbbreviation(abbreviation);
  const city = getTimezoneCityLabel(identifier);
  const baseName =
    primaryAbbreviation === "HST"
      ? "Hawaii Aleutian Time"
      : displayName
          ?.replace(/\s*\/\s*.*$/, "")
          .replace(/\bStandard\b|\bDaylight\b/g, "")
          .replace(/\s+/g, " ")
          .trim();

  if (!baseName) return city;
  return primaryAbbreviation
    ? `${baseName} (${primaryAbbreviation})`
    : baseName;
};

const formatUtcOffsetForDisplay = (utcOffset?: string): string | undefined =>
  utcOffset?.replace(/^UTC([+-])/, "UTC $1");

const normalizeTimezoneOption = (
  item: TimezoneMappingItem,
): SignupTimezoneOption | null => {
  const nestedDetails = item.details ?? undefined;
  const directDetails = item as TimezoneMappingDetails & { id?: unknown };
  const details = nestedDetails ?? directDetails;
  const rawId = item.id ?? (details as { id?: unknown }).id;
  const id =
    typeof rawId === "number" && Number.isFinite(rawId) ? rawId : undefined;
  if (id === undefined) return null;

  const abbreviation = getStringValue(details.name);
  const displayName = getStringValue(details.display_name);
  const identifier = getStringValue(details.tz_identifier);
  const utcOffset = identifier ? getTimezoneOffsetLabel(identifier) : undefined;
  const friendlyName = getFriendlyTimezoneName(
    displayName,
    abbreviation,
    identifier,
  );
  const formattedUtcOffset = formatUtcOffsetForDisplay(utcOffset);
  const title =
    [formattedUtcOffset ? `(${formattedUtcOffset})` : undefined, friendlyName]
      .filter(Boolean)
      .join(" ") ||
    displayName ||
    abbreviation ||
    identifier ||
    `Timezone ${id}`;
  const subtitle = getTimezoneCityLabel(identifier);
  const primaryAbbreviation = getPrimaryTimezoneAbbreviation(abbreviation);

  return {
    id,
    label: title,
    title,
    subtitle,
    utcOffset,
    abbreviation: primaryAbbreviation,
  };
};

export const formatTimezoneCardLabel = (
  option?: {
    utcOffset?: string;
    abbreviation?: string;
    title?: string;
  } | null,
): string | undefined => {
  if (!option) return undefined;
  const offset = formatUtcOffsetForDisplay(option.utcOffset);
  const abbr = getPrimaryTimezoneAbbreviation(option.abbreviation);
  if (offset && abbr) {
    return `(${offset}) (${abbr})`;
  }
  return option.title;
};

export const getTimezoneCardLabelFromMappings = (
  mappedData: unknown,
  timezoneId?: number | null,
): string | undefined => {
  if (!mappedData || typeof mappedData !== "object" || timezoneId == null) {
    return undefined;
  }

  const items = (mappedData as { items?: unknown[] }).items;
  if (!Array.isArray(items)) return undefined;

  for (const item of items) {
    const timezones = (
      item as { details?: { timezones?: CountryTimezoneMapping[] } }
    ).details?.timezones;
    if (!Array.isArray(timezones)) continue;

    const match = timezones.find((timezone) => timezone.id === timezoneId);
    if (match) {
      const identifier = getStringValue(match.tz_identifier);
      const utcOffset = identifier
        ? getTimezoneOffsetLabel(identifier)
        : undefined;
      const abbreviation = getStringValue(match.name);
      return formatTimezoneCardLabel({
        utcOffset,
        abbreviation,
        title: match.display_name ?? match.name ?? identifier,
      });
    }
  }

  return undefined;
};

export const getSignupTimezoneOptions = (
  mappingsResponse: unknown,
  isdCode: string,
): SignupTimezoneOption[] => {
  const options = getTimezoneItems(mappingsResponse, isdCode)
    .map(normalizeTimezoneOption)
    .filter((option): option is SignupTimezoneOption => option !== null);

  const uniqueOptions = new Map<number, SignupTimezoneOption>();
  for (const option of options) {
    uniqueOptions.set(option.id, option);
  }

  return [...uniqueOptions.values()];
};

const getCountryItems = (value: unknown): CountryMapping[] => {
  if (!value || typeof value !== "object") return [];
  const items = (value as { items?: unknown }).items;
  if (!Array.isArray(items)) return [];

  return items.filter(
    (item): item is CountryMapping =>
      item != null &&
      typeof item === "object" &&
      "details" in item &&
      (item as { details?: unknown }).details != null,
  );
};

const normalizeCodeDigits = (value: unknown): string =>
  typeof value === "string" ? value.replace(/\D/g, "") : "";

export const findCountryMappingByIsdCode = (
  mappingsResponse: unknown,
  isdCode?: string | null,
): CountryMapping | null => {
  const target = normalizeCodeDigits(isdCode);
  if (!target) return null;

  return (
    getCountryItems(mappingsResponse).find((country) => {
      const isdCodes = Array.isArray(country.details.isd_codes)
        ? country.details.isd_codes
        : [];

      return isdCodes.some((item) =>
        [item.name, item.display_name, item.code].some(
          (value) => normalizeCodeDigits(value) === target,
        ),
      );
    }) ?? null
  );
};

export const findCountryMappingByTimezoneId = (
  mappingsResponse: unknown,
  timezoneId?: number | null,
): CountryMapping | null => {
  if (timezoneId == null || !Number.isFinite(timezoneId) || timezoneId <= 0) {
    return null;
  }

  return (
    getCountryItems(mappingsResponse).find((country) => {
      const timezones = Array.isArray(country.details.timezones)
        ? country.details.timezones
        : [];

      return timezones.some((timezone) => timezone.id === timezoneId);
    }) ?? null
  );
};

export const getCountryTimezoneOptions = (
  country: CountryMapping | null,
): SignupTimezoneOption[] => {
  if (!country) return [];

  const timezones = Array.isArray(country.details.timezones)
    ? country.details.timezones
    : [];
  const uniqueOptions = new Map<number, SignupTimezoneOption>();

  for (const item of timezones) {
    const option = normalizeTimezoneOption(item);
    if (option) uniqueOptions.set(option.id, option);
  }

  return [...uniqueOptions.values()];
};
